import { TFunction } from 'i18next';

import { ContentLocalesI } from '~src/interfaces/session.types';
import {
  SurveyStatus,
  SurveySubjectTypes,
  SurveyTypes,
  SurveyUseCase,
} from '~src/interfaces/surveys.interfaces';
import { getSystemTranslations } from '~src/services/translationServices';

import { FilterTypes, StatusFilterTypes } from './constants/filterOptions';
import { MatrixReportResponseRaw } from '~src/interfaces/heatmap.interface';
import {CreateScheduleI} from "~src/interfaces/schedule.interface";

export enum SurveyScopes {
  Manager = 'manager',
  Admin = 'admin',
  User = 'user',
}

export interface SurveyQuestion {
  question_id: string;
  mandatory: boolean;
}

export interface GetSurveyResponse {
  items: Survey[];
}

export function getDefaultSurveyInvitation(t: TFunction): ContentLocalesI {
  return getSystemTranslations(t, 'x.survey.detail.edit.invitation.default');
}

export interface GetSurveysPayload {
  managerId: string;
  type: string;
  feedbackCycle: string;
  askId: string;
}

export interface GetSpiderGraphPayload {
  filterSubject: string;
  filterSurvey: string;
  filterAttributeOptions: string
  includeSelfAssessment: boolean;
  feedbackCycle: string;
  askSegmentIds: string;
}

export interface GetSpiderGraphResponse extends MatrixReportResponseRaw {}

// Survey as defined in responses from pulse-check-service
export interface Survey {
  id: string;
  createdAt: string;
  createdBy: string;
  description: ContentLocalesI;
  invitation: ContentLocalesI;
  name: ContentLocalesI;
  questionCount: number;
  questions?: SurveyQuestion[];
  subject: SurveySubjectTypes;
  topicCount: number;
  updatedAt: string;
  useCase: SurveyUseCase;
  scope: SurveyScopes;
}

export const surveyMaxLengthInvitation = 65536; // not a database restriction, just to prevent abuse

export interface SurveyExtended {
  id: string;
  name: ContentLocalesI;
  description?: ContentLocalesI | null;
  subject: SurveySubjectTypes;
  use_case?: SurveyUseCase;
  status: SurveyStatus;
  number_of_topics: number;
  number_of_questions: number;
  number_of_answers: number;
  response_rate: number;
  last_answered_at?: string | null;
  created_at: string;
  created_by: string;
  about?: Array<{
    id: string;
    image_url: string;
    meta: {
      email: string;
      firstname: string;
      lastname: string;
      manager_uuid?: string | null;
      name: string;
    };
    type: string;
  }>;
}

export interface SurveysListResp {
  result: {
    survey_list: SurveyExtended[];
    number_of_surveys: number;
    surveys_distribution: { [key in FilterTypes]: number };
    status_distribution: {
      active: number;
      draft: number;
    };
  };
  options: {
    url_values: {
      filterStatus: string[];
      sortBy: string[];
      sortOrder: string[];
    };
    survey_id?: any;
    topic_id?: any;
    question_id?: any;
    user_id?: any;
    group_id?: any;
    auth_tenant_id: string;
    auth_user_id: string;
  };
}

export interface FetchSurveysParams {
  ['filter.type']?: string;
  ['filter.feedbackCycle']?: string;
  filterSubject?: FilterTypes;
  filterStatus?: StatusFilterTypes;
  filterName?: string;
  page?: number | string;
  per_page?: number;
  sort?: string;
}

export interface NewManagerSurveySubmitData {
  survey: {
    name: ContentLocalesI;
    invitation: ContentLocalesI;
  };
  schedule: CreateScheduleI;
}

export interface CreateUserPullSurveySubmitData {
  survey: {
    name: Partial<ContentLocalesI>;
    invitation: ContentLocalesI;
  };
  schedule: CreateScheduleI;
}

export interface NewTeamSurveySubmitData {
  name: Partial<ContentLocalesI>;
  description?: Partial<ContentLocalesI>;
  invitation: ContentLocalesI;
}

export interface SurveyScheduleResponse {
  survey: Survey;
  schedule: {
    id: string;
    surveyId: string;
    title?: any;
    ask: any[];
    about: UserPullCreatedBy[];
    selfAssessment: boolean;
    anonymous: boolean;
    share: UserPullCreatedBy[];
    shareWithSubject: boolean;
    /** @deprecated */
    shareWithManagers: boolean;
    shareWithManagersLevel: number;
    attributesVisibility: boolean;
    showInHeatmapToprow: boolean;
    active: boolean;
    startDate?: any;
    repetition?: any;
    dueDate?: any;
    reminder?: any;
    overdueAnswers: boolean;
    startMail: boolean;
    repetitionMail: boolean;
    reminderMail: boolean;
    wasStarted: boolean;
    selfSelection: boolean;
    managersSelection: boolean;
    anonymityMinAnswers: number;
    createdAt: string;
    updatedAt: string;
    createdBy: UserPullCreatedBy;
  };
}

export interface CreateTeamSurveyResp {
  survey: {
    id: string;
    name: ContentLocalesI;
    description?: ContentLocalesI;
    subject: string;
    useCase: string;
    topicCount: number;
    questionCount: number;
    scope: string;
    createdBy: string;
    updatedBy: string;
    createdAt: string;
    updatedAt: string;
  };
  schedule: {
    id: string;
    surveyId: string;
    title?: any;
    ask: any[];
    about: UserPullCreatedBy[];
    selfAssessment: boolean;
    anonymous: boolean;
    share: UserPullCreatedBy[];
    shareWithSubject: boolean;
    /** @deprecated */
    shareWithManagers: boolean;
    shareWithManagersLevel: number;
    attributesVisibility: boolean;
    showInHeatmapToprow: boolean;
    active: boolean;
    startDate?: any;
    repetition?: any;
    dueDate?: any;
    reminder?: any;
    overdueAnswers: boolean;
    startMail: boolean;
    repetitionMail: boolean;
    reminderMail: boolean;
    wasStarted: boolean;
    selfSelection: boolean;
    managersSelection: boolean;
    anonymityMinAnswers: number;
    createdAt: string;
    updatedAt: string;
    createdBy: UserPullCreatedBy;
  };
}

interface UserPullCreatedBy {
  id: string;
  meta: {
    email: string;
    firstname: string;
    lastname: string;
    manager_uuid?: string;
    name: string;
  };
  type: string;
  image_url?: string;
}

export interface NewSurveySubmitData {
  name: ContentLocalesI;
  description?: ContentLocalesI;
  invitation: ContentLocalesI;
  questions?: SurveyQuestion[];
  subject?: SurveySubjectTypes | keyof SurveyTypes;
  useCase?: keyof Pick<SurveyTypes, 'bottom_up' | 'three_sixty' | 'top_down'>;
  scope?: SurveyScopes;
}

